import * as Sentry from '@sentry/react'
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { ReactNode, useContext, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useSessionStorage } from 'react-use'

import { useContentLock } from '@/app/student/hooks/useContentLock'
import { useLocalStorage } from '@/core/utils/localStorage/useLocalStorage'
import { env } from '@/env'
import { APP_LOCALE } from '@/locale/constants'
import { useLocale } from '@/locale/hooks/useLocale'
import { AppLocale } from '@/locale/types/Locale'
import { router } from '@/router/router'
import { useUpdateCurrentUser } from '@/user/mutations/useUpdateCurrentUser'

import { FEATURE_DEV_TOOLS } from '../constants/features'
import { FeatureContext } from '../utils/features/FeatureContext'
import useFeatures from '../utils/features/useFeatures'

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  borderRadius: '4px',
  border: '1px solid #555',
  background: '#333',
  color: 'white',
} as const

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h5
      style={{
        fontSize: '15px',
        fontWeight: 'bold',
        margin: '0 0 12px 0',
        paddingBottom: '8px',
        borderBottom: '1px solid #555',
      }}>
      {children}
    </h5>
  )
}

function FeatureFlags() {
  const features = useContext(FeatureContext)
  const [searchQuery, setSearchQuery] = useState('')

  const filteredFeatures =
    features?.filter((key) =>
      key.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || []

  return (
    <div>
      <SectionTitle>
        Feature Flags{' '}
        <span style={{ color: '#888', fontWeight: 'normal' }}>
          ({filteredFeatures.length})
        </span>
      </SectionTitle>
      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search features..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={inputStyle}
        />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: '4px 24px',
        }}>
        {filteredFeatures.map((flagName, index) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`${flagName}-${index}`}
            style={{
              padding: '4px 0',
              color: 'yellow',
              wordBreak: 'break-word',
            }}>
            {flagName}
          </div>
        ))}
        {filteredFeatures.length === 0 && (
          <div style={{ color: '#888' }}>No matching features</div>
        )}
      </div>
    </div>
  )
}

function getSessionReplayLink() {
  const replay = Sentry.getReplay()
  if (replay) {
    replay.flush()
    const replayId = replay.getReplayId()
    return `https://masterplan-com-gmbh.sentry.io/replays/${replayId}/`
  }
  return ''
}

function SessionReplay() {
  const sentryLink = useMemo(() => getSessionReplayLink(), [])

  return (
    <div>
      <SectionTitle>Session Replay</SectionTitle>
      <div>Sentry:</div>
      {sentryLink ? (
        <div style={{ marginTop: '4px' }}>
          <a
            href={sentryLink}
            target="_blank"
            style={{ color: '#cfc7ff', textDecoration: 'underline' }}
            rel="noopener noreferrer">
            {sentryLink}
          </a>{' '}
          🡕
        </div>
      ) : (
        <div style={{ color: 'yellow', marginTop: '4px' }}>
          Current session is not being recorded
        </div>
      )}
    </div>
  )
}

function Language() {
  const updateCurrentUser = useUpdateCurrentUser({ showToast: false })
  const [, setAudioLocaleStorage] = useLocalStorage('media-player-audio-locale')
  const currentLocale = useLocale()
  const languageNames = new Intl.DisplayNames(['en'], {
    type: 'language',
  })

  return (
    <div>
      <SectionTitle>Language</SectionTitle>
      <div style={{ marginBottom: '8px' }}>
        update status:{' '}
        <span style={{ color: 'yellow' }}>{updateCurrentUser.status}</span>
      </div>
      <select
        disabled={updateCurrentUser.isPending}
        value={currentLocale}
        onChange={(e) => {
          updateCurrentUser.mutate(
            {
              data: { lang: e.target.value as AppLocale },
            },
            { onSuccess: () => setAudioLocaleStorage(e.target.value) },
          )
        }}
        style={inputStyle}>
        {APP_LOCALE.map((langCode) => (
          <option key={langCode} value={langCode}>
            {languageNames.of(langCode)}
          </option>
        ))}
      </select>
    </div>
  )
}

function ContentLock() {
  const { lockContent } = useContentLock()
  return (
    <div>
      <SectionTitle>Lock Content</SectionTitle>
      <div>
        Status:{' '}
        <span style={{ color: 'yellow' }}>{lockContent ? 'On' : 'Off'}</span>
      </div>
      <div>
        <button
          style={{
            background: 'none',
            border: '1px solid #fff',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '8px',
          }}
          type="button"
          onClick={() => {
            if (!lockContent) {
              const url = new URL(window.location.href)
              url.searchParams.set('lock_content', 'true')
              window.location.href = url.toString()
            } else {
              sessionStorage.removeItem('lock_content')
              const url = new URL(window.location.href)
              url.searchParams.delete('lock_content')
              window.location.href = url.toString()
            }
          }}>
          {lockContent ? 'Deactivate Content Lock' : 'Activate Content Lock'}
        </button>
      </div>
    </div>
  )
}

type DevToolsTab =
  | 'features'
  | 'session-replay'
  | 'content-lock'
  | 'language'
  | 'react-query'
  | 'react-router'

const APP_TABS: {
  id: DevToolsTab
  label: string
  render: () => JSX.Element
}[] = [
  { id: 'features', label: 'Feature Flags', render: () => <FeatureFlags /> },
  {
    id: 'session-replay',
    label: 'Session Replay',
    render: () => <SessionReplay />,
  },
  {
    id: 'content-lock',
    label: 'Content Lock',
    render: () => <ContentLock />,
  },
  { id: 'language', label: 'Language', render: () => <Language /> },
]

function Panel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: '16px',
        height: '100%',
        width: '100%',
        overflow: 'auto',
      }}>
      {children}
    </div>
  )
}

function navButtonStyle(isActive: boolean) {
  return {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: isActive ? '#444' : 'none',
    border: 'none',
    borderLeft: `3px solid ${isActive ? '#cfc7ff' : 'transparent'}`,
    color: 'white',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '14px',
  } as const
}

export function DevToolsPanel() {
  const hasDevTools = useFeatures(FEATURE_DEV_TOOLS)
  const [isAppDevToolsOpen, setIsAppDevToolsOpen] = useSessionStorage(
    'devtools-visible',
    false,
  )
  const [activeTab, setActiveTab] = useState<DevToolsTab>('features')

  if (!hasDevTools) {
    return null
  }

  const isDevelopment = env.MODE === 'development'

  return (
    <>
      {isAppDevToolsOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '0',
            left: '0',
            right: '0',
            height: '500px',
            display: 'flex',
            background: 'rgba(0, 0, 0, 1)',
            color: 'white',
            borderTop: '1px solid #fff',
            zIndex: 99999,
          }}>
          <nav
            style={{
              width: '180px',
              flexShrink: 0,
              borderRight: '1px solid #555',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
            }}>
            <h4
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                margin: '0',
                padding: '12px',
              }}>
              🛠️ DevTools
            </h4>
            {APP_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                style={navButtonStyle(activeTab === tab.id)}
                onClick={() => setActiveTab(tab.id)}>
                {tab.label}
              </button>
            ))}
            {isDevelopment && (
              <button
                type="button"
                style={navButtonStyle(activeTab === 'react-query')}
                onClick={() => setActiveTab('react-query')}>
                React Query
              </button>
            )}
            {isDevelopment && (
              <button
                type="button"
                style={navButtonStyle(activeTab === 'react-router')}
                onClick={() => setActiveTab('react-router')}>
                React Router
              </button>
            )}
            <div
              style={{
                marginTop: 'auto',
                padding: '12px',
                fontSize: '12px',
                color: '#888',
                borderTop: '1px solid #555',
              }}>
              Environment: {env.MODE}
            </div>
          </nav>

          <div
            style={{
              flex: 1,
              minWidth: 0,
              position: 'relative',
              overflow: 'hidden',
            }}>
            {APP_TABS.map(
              (tab) =>
                activeTab === tab.id && (
                  <Panel key={tab.id}>{tab.render()}</Panel>
                ),
            )}
            {isDevelopment && activeTab === 'react-query' && (
              <ReactQueryDevtoolsPanel
                style={{ height: '100%' }}
                onClose={() => setActiveTab('features')}
              />
            )}
            {isDevelopment && activeTab === 'react-router' && (
              <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
                <TanStackRouterDevtoolsPanel router={router} />
              </div>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsAppDevToolsOpen(!isAppDevToolsOpen)}
        aria-label={isAppDevToolsOpen ? 'Hide DevTools' : 'Show DevTools'}
        title={isAppDevToolsOpen ? 'Hide DevTools' : 'Show DevTools'}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '34px',
          height: '34px',
          padding: '0',
          fontSize: '14px',
          background: '#444444',
          color: 'white',
          borderRadius: '4px',
          cursor: 'pointer',
          zIndex: 100000,
        }}>
        🛠️
      </button>
    </>
  )
}

export function DevTools() {
  return (
    <ErrorBoundary fallback={null}>
      <DevToolsPanel />
    </ErrorBoundary>
  )
}
